import { useMutation, useQuery } from '@apollo/client';
import * as icons from '@mui/icons-material';
import {
  Card,
  CircularProgress,
  IconButton,
  LinearProgress,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { FormSubmitRow } from 'components/common/form/submit-row';
import { TooltipIconButton } from 'components/common/icon/tooltip-icon-button';
import { MetadataItem } from 'components/common/metadata';
import { MetadataItemCopy } from 'components/common/metadata/metadata-item';
import { FormTextField } from 'components/views/forms/form-text-field';
import { FormEvent, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { AssignCampaign, AssignCampaignVariables } from 'types/apollo/AssignCampaign';
import { TaskStatus, TaskStatusVariables } from 'types/apollo/TaskStatus';
import { TcrPhone, TcrPhoneVariables } from 'types/apollo/TcrPhone';
import { TcrPhones, TcrPhones_tcrPhones, TcrPhonesVariables } from 'types/apollo/TcrPhones';
import { formatDate } from 'utils/date';
import {
  A2pAssignCampaignMutation,
  A2pPhoneQuery,
  A2pPhonesQuery,
  A2pPhoneTaskStatusQuery,
} from './a2p.graphql';

interface AssignCampaignFormData {
  tcrCampaign: string;
}

interface StatusRowProps {
  data: TcrPhones_tcrPhones;
  taskStatus: string | null;
  isPolling: boolean;
  stopPolling: () => void;
}

const A2pStatus = ({ status, polling }: { status: string | null; polling: boolean }) => {
  const icon =
    status == 'ADDED' ? (
      <icons.CheckCircleOutlineOutlined color="success" />
    ) : status == 'PENDING' || status == 'ASSIGNING' || polling ? (
      <CircularProgress size={18} />
    ) : (
      <icons.ErrorOutline color="error" />
    );
  return (
    <Typography component="span" fontSize={22} textAlign="left">
      {icon}
      {status ?? 'NOT REGISTERED'}
    </Typography>
  );
};

const StatusRow = ({ data, taskStatus, stopPolling, isPolling }: StatusRowProps) => {
  return (
    <Stack direction="row">
      <MetadataItem
        label="T-Mobile status"
        value={<A2pStatus status={data.tmobileNumberMappingStatus} polling={isPolling} />}
      />
      <MetadataItem
        label="Non T-Mobile status"
        value={<A2pStatus status={data.nonTmobileNumberMappingStatus} polling={isPolling} />}
      />
      {taskStatus && (
        <MetadataItem
          label="Task status"
          value={
            taskStatus == 'pending' ? (
              <>
                <CircularProgress size={18} />
                pending...
              </>
            ) : (
              taskStatus
            )
          }
        />
      )}
      {isPolling && (
        <TooltipIconButton
          onClick={stopPolling}
          tooltip="Cancel polling"
          iconButtonProps={{ sx: { height: 40, width: 40 } }}
        >
          <icons.CancelOutlined />
        </TooltipIconButton>
      )}
    </Stack>
  );
};

const AssignCampaignForm = (props: {
  fetchTask: (taskId: string) => Promise<void>;
  setTaskStatus: (status: string) => void;
  disabled: boolean;
  campaignId: string;
  tcrCampaignId: string | null;
}) => {
  const form = useForm<AssignCampaignFormData>({
    defaultValues: {
      tcrCampaign: props.tcrCampaignId ?? '',
    },
  });

  const [assignCampaign] = useMutation<AssignCampaign, AssignCampaignVariables>(
    A2pAssignCampaignMutation,
  );

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    props.setTaskStatus('assigning...');

    const tcrCampaignId = form.getValues('tcrCampaign');
    const response = await assignCampaign({
      variables: {
        campaignId: props.campaignId,
        tcrCampaignId: tcrCampaignId,
      },
    });

    const newTaskId = response.data?.assignCampaign?.taskId;
    if (newTaskId) {
      await props.fetchTask(newTaskId);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <FormProvider {...form}>
        <FormTextField form={form} name="tcrCampaign" label="TCR Campaign ID" />
        <FormSubmitRow submitText="Assign" enableIfNotDirty disabled={props.disabled} />
      </FormProvider>
    </form>
  );
};

interface A2pCardProps {
  campaignId: string;
  internalCampaignId: string;
  phoneRecord: TcrPhones_tcrPhones;
}

const A2pCard = ({ internalCampaignId, phoneRecord }: A2pCardProps) => {
  const [data, setData] = useState(phoneRecord);
  const [isPolling, setIsPolling] = useState(false);
  const [taskStatus, setTaskStatus] = useState<string | null>(null);

  const {
    refetch: fetchPhone,
    startPolling: startPhonePolling,
    stopPolling: stopPhonePolling,
  } = useQuery<TcrPhone, TcrPhoneVariables>(A2pPhoneQuery, {
    variables: {
      phoneNumber: phoneRecord.phoneNumber,
    },
    skip: true,
    nextFetchPolicy: 'cache-and-network',
    onCompleted: (response) => {
      if (response) {
        const tcrPhone = response.tcrPhone;
        setData(tcrPhone);

        const tmobileIsTerminal =
          tcrPhone.tmobileNumberMappingStatus == 'ADDED' ||
          tcrPhone.tmobileNumberMappingStatus == 'FAILED';
        const nonMobileIsTerminal =
          tcrPhone.nonTmobileNumberMappingStatus == 'ADDED' ||
          tcrPhone.nonTmobileNumberMappingStatus == 'FAILED';

        if (isPolling && tmobileIsTerminal && nonMobileIsTerminal) {
          stopPolling();
        }
      }
    },
  });

  const {
    refetch: fetchTask,
    startPolling: startTaskPolling,
    stopPolling: stopTaskPolling,
  } = useQuery<TaskStatus, TaskStatusVariables>(A2pPhoneTaskStatusQuery, {
    skip: true,
    nextFetchPolicy: 'cache-and-network',
    onCompleted: async (response) => {
      if (response) {
        const status = response.tcrTaskStatus.find(
          (s) => s.phoneNumber == data.phoneNumber,
        )?.status;

        if (status) {
          setTaskStatus(status);
        }

        if (status == 'completed') {
          setData((prevState) => ({
            ...prevState,
            tmobileNumberMappingStatus:
              prevState.tmobileNumberMappingStatus != 'ADDED'
                ? 'ASSIGNING'
                : prevState.tmobileNumberMappingStatus,
            nonTmobileNumberMappingStatus:
              prevState.nonTmobileNumberMappingStatus != 'ADDED'
                ? 'ASSIGNING'
                : prevState.nonTmobileNumberMappingStatus,
          }));

          stopTaskPolling();
          startPhonePolling(5000);
        } else if (status == 'failed') {
          stopPolling();
        }
      }
    },
  });

  const stopPolling = () => {
    setIsPolling(false);
    stopTaskPolling();
    stopPhonePolling();
  };

  const startFetchingTask = async (taskId: string) => {
    setIsPolling(true);
    await new Promise((resolve) => setTimeout(resolve, 5000));
    await fetchTask({ taskId });
    startTaskPolling(10000);
  };

  const isTcr = (data.campaignId?.length ?? 0) < 10;
  const campaignIdLabel = isTcr ? 'TCR Campaign ID' : 'Telnyx Campaign ID';
  const link = isTcr
    ? `https://csp.campaignregistry.com/campaigns/${data.campaignId}`
    : `https://portal.telnyx.com/#/app/messaging-10dlc/campaigns/${data.campaignId}`;

  return (
    <Card variant="outlined" sx={{ padding: 2, marginTop: 2 }}>
      <Typography variant="h5">
        A2P registration{' '}
        <IconButton onClick={() => fetchPhone()}>
          <icons.Refresh />
        </IconButton>
      </Typography>
      <Typography variant="h6">{data.phoneNumber}</Typography>
      {data.campaignId && (
        <MetadataItemCopy
          label={campaignIdLabel}
          valueToCopy={data.campaignId}
          value={
            <Link target="_blank" href={link}>
              {data.campaignId}
              <icons.OpenInNewOutlined color="action" fontSize="small" />
            </Link>
          }
        />
      )}
      <StatusRow
        data={data}
        isPolling={isPolling}
        taskStatus={taskStatus}
        stopPolling={stopPolling}
      />
      {data.errors && (
        <MetadataItemCopy
          label="Error"
          value={<Typography color="error">{data.errors}</Typography>}
          valueToCopy={data.errors}
        />
      )}
      <Stack direction="row" sx={{ minWidth: 400, maxWidth: '60%' }}>
        {data.updatedAt && <MetadataItem label="Updated" value={formatDate(data.updatedAt)} />}
        {data.createdAt && <MetadataItem label="Created" value={formatDate(data.createdAt)} />}
      </Stack>
      {isTcr &&
        (data.tmobileNumberMappingStatus != 'ADDED' ||
          data.nonTmobileNumberMappingStatus != 'ADDED') && (
          <AssignCampaignForm
            setTaskStatus={setTaskStatus}
            fetchTask={startFetchingTask}
            campaignId={internalCampaignId}
            tcrCampaignId={data.campaignId}
            disabled={isPolling}
          />
        )}
    </Card>
  );
};

interface A2pCardsProps {
  campaignId: string;
  internalCampaignId: string;
}

export const A2pCards = ({ campaignId, internalCampaignId }: A2pCardsProps) => {
  const { data, loading } = useQuery<TcrPhones, TcrPhonesVariables>(A2pPhonesQuery, {
    variables: {
      campaignId: internalCampaignId,
    },
    nextFetchPolicy: 'cache-and-network',
  });

  if (loading || !data?.tcrPhones) {
    return (
      <Card variant="outlined" sx={{ padding: 2, marginTop: 2 }}>
        <Typography variant="h5">A2P registration</Typography>
        <LinearProgress sx={{ height: '2px', mt: 1 }} />
      </Card>
    );
  }

  return (
    <>
      {data.tcrPhones.map((p) => (
        <A2pCard
          key={p.phoneNumber}
          phoneRecord={p}
          campaignId={campaignId}
          internalCampaignId={internalCampaignId}
        />
      ))}
    </>
  );
};
